import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  CalendarClock,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Swords,
} from "lucide-react"
import { useEffect, useState } from "react"

import type {
  GuildWarWeeklySignupState,
  GuildWarWeekState,
} from "@/core/guild-war/types"

import { Button } from "@/components/button"
import { deriveGuildWarSignupActionState } from "@/components/guild-war/guild-war-signup-state"
import { PathSelector } from "@/components/guild-war/path-selector"
import { PageIntroSplitHeader } from "@/components/layout/page-intro-split-header"
import { CardHeading } from "@/components/shared/card-heading"
import { CountdownTimer } from "@/components/shared/countdown-timer"
import { InlineStatus } from "@/components/shared/inline-status"
import { MetricTile } from "@/components/shared/metric-tile"
import { SectionKicker } from "@/components/shared/section-kicker"
import { StatusAlert } from "@/components/shared/status-alert"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "@/components/ui/card"
import { PageHeaderSkeleton } from "@/components/ui/page-skeletons"
import { formatWeekendRange } from "@/core/guild-war/weekend-dates"
import {
  guildWarApi,
  type MartialArtPath,
  type WeeklyAvailability,
} from "@/lib/guild-war-api"
import { toGuildWarPathPairDisplayLabel } from "@/lib/guild-war-path-display"
import { guildWarKeys } from "@/lib/query-keys"

const dashboardQueryKey = guildWarKeys.meDashboard()

const availabilityLabel: Record<WeeklyAvailability, string> = {
  saturday: "Saturday",
  sunday: "Sunday",
  both: "Both",
}

const weekStateLabel: Record<GuildWarWeekState, string> = {
  draft: "Draft",
  signup_open: "Signup Open",
  planning: "Planning",
  completed: "Completed",
}

const formatWeekStateForDisplay = (
  state: GuildWarWeekState | null | undefined,
): string => {
  return state ? weekStateLabel[state] : "No signup"
}

export function GuildWarSignupPage({ userId }: { userId: string }) {
  const queryClient = useQueryClient()
  const [notice, setNotice] = useState<{
    tone: "success" | "error"
    text: string
  } | null>(null)
  const [primaryPath, setPrimaryPath] = useState<MartialArtPath | "none">(
    "none",
  )
  const [secondaryPath, setSecondaryPath] = useState<MartialArtPath | "none">(
    "none",
  )
  const [pendingAvailability, setPendingAvailability] =
    useState<WeeklyAvailability | null>(null)
  const [primaryPathError, setPrimaryPathError] = useState<string | null>(null)
  const [availabilityError, setAvailabilityError] = useState<string | null>(
    null,
  )
  const [isConfirmWeeklySignupOpen, setIsConfirmWeeklySignupOpen] =
    useState(false)

  const dashboardQuery = useQuery({
    queryKey: dashboardQueryKey,
    queryFn: guildWarApi.getMyDashboard,
  })

  const dashboard = dashboardQuery.data
  const isDashboardReady =
    dashboardQuery.isSuccess && !dashboardQuery.isFetching
  const isDashboardLoading = dashboardQuery.isPending
  const isDashboardRefreshing =
    dashboardQuery.isFetching && !dashboardQuery.isPending
  const week = dashboard?.week ?? null
  const hasActiveWeeklySignup = dashboard?.weeklySignup?.state === "active"
  const hasPendingPermanentApplication =
    dashboard?.pendingPermanentApplication?.state === "pending"
  const isPermanentMember = dashboard?.isPermanentMember ?? false

  const selectedAvailability = hasActiveWeeklySignup
    ? (dashboard?.weeklySignup?.availability ?? null)
    : null
  const selectedOrPendingAvailability =
    pendingAvailability ?? selectedAvailability
  const hasPendingAvailabilityChange =
    pendingAvailability !== null && pendingAvailability !== selectedAvailability

  useEffect(() => {
    setPrimaryPath(
      (dashboard?.profile?.primaryPathCode as MartialArtPath) ?? "none",
    )
  }, [dashboard?.profile?.primaryPathCode])

  useEffect(() => {
    setSecondaryPath(
      (dashboard?.profile?.secondaryPathCode as MartialArtPath) ?? "none",
    )
  }, [dashboard?.profile?.secondaryPathCode])

  const validateWeeklySignupSubmission = (): boolean => {
    let isValid = true

    if (primaryPath === "none") {
      setPrimaryPathError(
        "Choose a primary path before submitting weekly signup.",
      )
      isValid = false
    } else {
      setPrimaryPathError(null)
    }

    if (!selectedOrPendingAvailability) {
      setAvailabilityError(
        "Choose Saturday, Sunday, or Both before continuing.",
      )
      isValid = false
    } else {
      setAvailabilityError(null)
    }

    return isValid
  }

  const profileMutation = useMutation({
    mutationFn: guildWarApi.setMyProfilePaths,
    onSuccess: async () => {
      setNotice({ tone: "success", text: "Updated your default skill paths." })
      await queryClient.invalidateQueries({ queryKey: dashboardQueryKey })
    },
    onError: (error) => {
      setNotice({
        tone: "error",
        text:
          error instanceof Error
            ? error.message
            : "Failed to update your profile paths",
      })
    },
  })

  const weeklySignupMutation = useMutation({
    mutationFn: async (availability: WeeklyAvailability) => {
      if (!week) {
        throw new Error("An active or upcoming week is required for signup")
      }

      if (primaryPath === "none") {
        throw new Error("A primary path is required for signup")
      }

      return guildWarApi.setWeeklySignupForMember(userId, {
        weekId: week.id,
        availability,
        skillPath: primaryPath,
        secondarySkillPath: secondaryPath === "none" ? null : secondaryPath,
      })
    },
    onSuccess: async (_result, availability) => {
      setNotice({
        tone: "success",
        text: `Saved weekly signup as ${availabilityLabel[availability]}.`,
      })
      setPendingAvailability(null)
      setAvailabilityError(null)
      setIsConfirmWeeklySignupOpen(false)
      await queryClient.invalidateQueries({ queryKey: dashboardQueryKey })
    },
    onError: (error) => {
      setNotice({
        tone: "error",
        text:
          error instanceof Error
            ? error.message
            : "Failed to save weekly signup",
      })
    },
  })

  const withdrawWeeklyMutation = useMutation({
    mutationFn: async () => {
      if (!week) {
        throw new Error("An active or upcoming week is required")
      }

      return guildWarApi.withdrawWeeklySignupForMember(userId, week.id)
    },
    onSuccess: async () => {
      setNotice({ tone: "success", text: "Withdrew your weekly signup." })
      await queryClient.invalidateQueries({ queryKey: dashboardQueryKey })
    },
    onError: (error) => {
      setNotice({
        tone: "error",
        text:
          error instanceof Error
            ? error.message
            : "Failed to withdraw weekly signup",
      })
    },
  })

  const applyPermanentMutation = useMutation({
    mutationFn: async () => {
      const expiresAt = week
        ? new Date(week.weekEndAt)
        : new Date(Date.now() + 14 * 24 * 60 * 60 * 1000)

      if (primaryPath === "none") {
        throw new Error(
          "A primary path is required for permanent membership application",
        )
      }

      return guildWarApi.applyPermanentMembership({
        weekId: week?.id,
        expiresAt,
        skillPathAtApply: primaryPath,
        note: null,
      })
    },
    onSuccess: async () => {
      setNotice({
        tone: "success",
        text: "Submitted your permanent membership application.",
      })
      await queryClient.invalidateQueries({ queryKey: dashboardQueryKey })
    },
    onError: (error) => {
      setNotice({
        tone: "error",
        text:
          error instanceof Error
            ? error.message
            : "Failed to submit permanent membership application",
      })
    },
  })

  const withdrawPermanentMutation = useMutation({
    mutationFn: guildWarApi.withdrawMyPermanent,
    onSuccess: async (result) => {
      setNotice({
        tone: "success",
        text:
          result.target === "membership"
            ? "Withdrew your active permanent membership."
            : "Withdrew your pending permanent membership application.",
      })
      await queryClient.invalidateQueries({ queryKey: dashboardQueryKey })
    },
    onError: (error) => {
      setNotice({
        tone: "error",
        text:
          error instanceof Error
            ? error.message
            : "Failed to withdraw permanent status",
      })
    },
  })

  const weeklySkillLabel = hasActiveWeeklySignup
    ? toGuildWarPathPairDisplayLabel(
        dashboard?.profile?.primaryPathCode ?? null,
        dashboard?.profile?.secondaryPathCode ?? null,
        {
          variant: "short",
          noneLabel: "Not set",
        },
      )
    : `${toGuildWarPathPairDisplayLabel(primaryPath, secondaryPath, {
        variant: "short",
        noneLabel: "Not set",
      })} (profile default)`

  const selectedAvailabilityLabel = selectedOrPendingAvailability
    ? availabilityLabel[selectedOrPendingAvailability]
    : "Not selected"

  const hasPendingMutation =
    profileMutation.isPending ||
    weeklySignupMutation.isPending ||
    withdrawWeeklyMutation.isPending ||
    applyPermanentMutation.isPending ||
    withdrawPermanentMutation.isPending

  const {
    canEditWeeklySignup,
    canApplyPermanent,
    canWithdrawPermanent,
    statusMessage,
  } = deriveGuildWarSignupActionState({
    isDashboardReady,
    isDashboardLoading,
    isDashboardRefreshing,
    hasPendingMutation,
    weekState: week?.state ?? null,
    isPermanentMember,
    hasActiveWeeklySignup,
    hasPendingPermanentApplication,
  })

  const weekLabel = week
    ? formatWeekendRange({ weekStartAt: week.weekStartAt })
    : "Not set"

  return (
    <div className="grid gap-4 md:gap-5">
      {dashboardQuery.isPending ? (
        <PageHeaderSkeleton />
      ) : (
        <Card className="page-intro border-primary/10">
          <PageIntroSplitHeader
            kicker={
              <SectionKicker icon={<Swords />}>Guild War Signup</SectionKicker>
            }
            title="Manage your weekly guild war commitments"
            titleClassName="text-4xl leading-none font-semibold md:text-5xl"
            description="This dashboard mirrors the Discord signup flow in web form. Set your default paths, choose weekly availability, and manage permanent status in one place."
            descriptionClassName="max-w-3xl text-base leading-7"
            metrics={
              <>
                <MetricTile label="Week" value={weekLabel} />
                <MetricTile
                  label="State"
                  value={
                    week?.state
                      ? formatWeekStateForDisplay(week.state)
                      : "No week"
                  }
                />
                <MetricTile
                  label="Permanent Membership"
                  value={
                    isPermanentMember
                      ? "Active"
                      : hasPendingPermanentApplication
                        ? "Pending"
                        : "No"
                  }
                />
              </>
            }
          />
        </Card>
      )}

      {dashboardQuery.isError ? (
        <StatusAlert tone="danger">
          <div className="flex items-center gap-3">
            <span className="flex-1">
              {dashboardQuery.error instanceof Error
                ? dashboardQuery.error.message
                : "Failed to load guild war dashboard"}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="shrink-0"
              onClick={() => void dashboardQuery.refetch()}
            >
              <RefreshCw className="size-3.5" />
              Retry
            </Button>
          </div>
        </StatusAlert>
      ) : null}

      {notice ? (
        <InlineStatus tone={notice.tone}>{notice.text}</InlineStatus>
      ) : null}

      {isDashboardReady ? (
        <>
          <Card>
            <CardHeader>
              <CardHeading icon={<Sparkles className="size-5 text-primary" />}>
                Profile Skill Paths
              </CardHeading>
              <CardDescription>
                These are your default paths used for weekly signup unless you
                override them in another workflow.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] md:items-center">
              <PathSelector
                value={primaryPath}
                label="Primary path"
                required
                hint="Used as your baseline path for weekly signup and permanent applications."
                error={primaryPathError ?? undefined}
                disabled={!isDashboardReady || hasPendingMutation}
                onValueChange={(value) => {
                  setNotice(null)
                  setPrimaryPathError(null)
                  setPrimaryPath(value)
                  if (value !== "none" && secondaryPath === value) {
                    setSecondaryPath("none")
                  }
                }}
              />

              <PathSelector
                value={secondaryPath}
                label="Secondary path"
                hint="Optional backup path used for roster balancing."
                disabled={!isDashboardReady || hasPendingMutation}
                excludeValues={primaryPath === "none" ? [] : [primaryPath]}
                onValueChange={(value) => {
                  setNotice(null)
                  setSecondaryPath(value)
                }}
              />

              <div className="flex items-end justify-start">
                <Button
                  type="button"
                  disabled={!isDashboardReady || hasPendingMutation}
                  className="w-full md:min-w-36"
                  onClick={() => {
                    setNotice(null)
                    void profileMutation.mutateAsync({
                      primaryPath: primaryPath === "none" ? null : primaryPath,
                      secondaryPath:
                        secondaryPath === "none" ? null : secondaryPath,
                    })
                  }}
                >
                  {profileMutation.isPending ? "Saving..." : "Save paths"}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardHeading
                icon={<CalendarClock className="size-5 text-primary" />}
              >
                Weekly Signup
              </CardHeading>
              <CardDescription>
                {statusMessage}
                <br />
                <span className="text-muted-foreground">
                  Martial Path(s): {weeklySkillLabel}
                </span>
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4">
              {week ? (
                <CountdownTimer
                  targetDate={new Date(week.weekEndAt)}
                  label="Signup closes in"
                  expiredLabel="Signup deadline passed"
                  compact
                  className="w-fit"
                />
              ) : null}
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  disabled={!canEditWeeklySignup || hasPendingMutation}
                  variant={
                    selectedOrPendingAvailability === "saturday"
                      ? "default"
                      : "outline"
                  }
                  onClick={() => {
                    setNotice(null)
                    setAvailabilityError(null)
                    setPendingAvailability("saturday")
                  }}
                >
                  {selectedOrPendingAvailability === "saturday"
                    ? "✅ Saturday"
                    : "Saturday"}
                </Button>
                <Button
                  type="button"
                  disabled={!canEditWeeklySignup || hasPendingMutation}
                  variant={
                    selectedOrPendingAvailability === "sunday"
                      ? "default"
                      : "outline"
                  }
                  onClick={() => {
                    setNotice(null)
                    setAvailabilityError(null)
                    setPendingAvailability("sunday")
                  }}
                >
                  {selectedOrPendingAvailability === "sunday"
                    ? "✅ Sunday"
                    : "Sunday"}
                </Button>
                <Button
                  type="button"
                  disabled={!canEditWeeklySignup || hasPendingMutation}
                  variant={
                    selectedOrPendingAvailability === "both"
                      ? "default"
                      : "outline"
                  }
                  onClick={() => {
                    setNotice(null)
                    setAvailabilityError(null)
                    setPendingAvailability("both")
                  }}
                >
                  {selectedOrPendingAvailability === "both"
                    ? "✅ Both"
                    : "Both"}
                </Button>
              </div>

              {availabilityError ? (
                <p className="text-xs text-destructive" role="alert">
                  {availabilityError}
                </p>
              ) : null}

              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  disabled={
                    !canEditWeeklySignup ||
                    hasPendingMutation ||
                    !hasPendingAvailabilityChange ||
                    primaryPath === "none"
                  }
                  onClick={() => {
                    setNotice(null)
                    if (!validateWeeklySignupSubmission()) {
                      return
                    }

                    setIsConfirmWeeklySignupOpen(true)
                  }}
                >
                  {weeklySignupMutation.isPending
                    ? "Submitting..."
                    : "Confirm & submit"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={!canEditWeeklySignup || hasPendingMutation}
                  onClick={() => {
                    setNotice(null)
                    setPendingAvailability(null)
                    setAvailabilityError(null)
                  }}
                >
                  Clear selection
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  disabled={
                    !hasActiveWeeklySignup ||
                    !canEditWeeklySignup ||
                    hasPendingMutation
                  }
                  onClick={() => {
                    setNotice(null)
                    void withdrawWeeklyMutation.mutateAsync()
                  }}
                >
                  {withdrawWeeklyMutation.isPending
                    ? "Withdrawing..."
                    : "Withdraw"}
                </Button>
              </div>

              {primaryPath === "none" && canEditWeeklySignup ? (
                <p className="text-xs text-muted-foreground">
                  Set a primary path above to enable signup.
                </p>
              ) : null}

              {hasPendingAvailabilityChange ? (
                <InlineStatus tone="success">
                  {`Selected: ${selectedAvailabilityLabel}. Review and submit to save.`}
                </InlineStatus>
              ) : null}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardHeading
                icon={<ShieldCheck className="size-5 text-primary" />}
              >
                Permanent Membership
              </CardHeading>
              <CardDescription>
                Apply for permanent membership when you want to stay in the
                roster pool continuously
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              <Button
                type="button"
                disabled={
                  !canApplyPermanent ||
                  hasPendingMutation ||
                  primaryPath === "none"
                }
                onClick={() => {
                  setNotice(null)
                  void applyPermanentMutation.mutateAsync()
                }}
              >
                {isPermanentMember
                  ? "✅ Permanent Membership active"
                  : hasPendingPermanentApplication
                    ? "⏳ Applied for permanent membership"
                    : applyPermanentMutation.isPending
                      ? "Applying..."
                      : "Apply for permanent membership"}
              </Button>
              <Button
                type="button"
                variant="destructive"
                disabled={!canWithdrawPermanent || hasPendingMutation}
                onClick={() => {
                  setNotice(null)
                  void withdrawPermanentMutation.mutateAsync()
                }}
              >
                {withdrawPermanentMutation.isPending
                  ? "Withdrawing..."
                  : isPermanentMember
                    ? "Withdraw Permanent Membership"
                    : "Withdraw Permanent Membership Application"}
              </Button>
              {primaryPath === "none" && canApplyPermanent ? (
                <p className="text-xs text-muted-foreground">
                  Set a primary path above to apply for permanent membership.
                </p>
              ) : null}
            </CardContent>
          </Card>
        </>
      ) : null}

      <AlertDialog
        open={isConfirmWeeklySignupOpen}
        onOpenChange={setIsConfirmWeeklySignupOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm your weekly signup</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to submit{" "}
              <strong>{selectedAvailabilityLabel}</strong> with
              <strong>
                {" "}
                {toGuildWarPathPairDisplayLabel(primaryPath, secondaryPath, {
                  variant: "short",
                  noneLabel: "Not set",
                })}
              </strong>{" "}
              for {weekLabel}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={weeklySignupMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={
                weeklySignupMutation.isPending ||
                selectedOrPendingAvailability === null ||
                !hasPendingAvailabilityChange ||
                primaryPath === "none"
              }
              onClick={() => {
                if (!selectedOrPendingAvailability) {
                  return
                }

                setNotice(null)
                void weeklySignupMutation.mutateAsync(
                  selectedOrPendingAvailability,
                )
              }}
            >
              {weeklySignupMutation.isPending ? "Submitting..." : "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
